import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Check, X, Play, RefreshCw } from "lucide-react";
import { useState } from "react";

export const InvestmentsSection = ({ investments, users, packages, fetchData }) => {
  const [processingMatured, setProcessingMatured] = useState(false);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const handleProcessMatured = async () => {
    setProcessingMatured(true);
    try {
      const res = await axios.post(`${API}/admin/process-matured-investments`, {}, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(res.data.message);
      fetchData();
    } catch { toast.error("Failed to process matured investments"); }
    finally { setProcessingMatured(false); }
  };

  const handleApproveMaturity = async (investmentId) => {
    try {
      const res = await axios.put(`${API}/admin/investments/${investmentId}/approve-maturity`, {}, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve maturity");
    }
  };

  const handleRejectMaturity = async (investmentId) => {
    try {
      const res = await axios.put(`${API}/admin/investments/${investmentId}/reject-maturity`, {}, { headers: getAuthHeaders(), withCredentials: true });
      toast.success(res.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject maturity");
    }
  };

  return (
    <Card className="bg-white border-slate-200">
      <CardHeader className="flex flex-row items-center justify-between border-b border-slate-100">
        <CardTitle className="text-slate-800 font-heading">Investments</CardTitle>
        <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-600 hover:bg-emerald-50 gap-1.5" onClick={handleProcessMatured} disabled={processingMatured} data-testid="process-matured-btn">
          {processingMatured ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          Detect Matured
        </Button>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left text-slate-500 text-xs uppercase tracking-wider bg-slate-50">
                <th className="px-5 py-3 font-semibold">ID</th>
                <th className="px-5 py-3 font-semibold">User</th>
                <th className="px-5 py-3 font-semibold">Package</th>
                <th className="px-5 py-3 font-semibold">Amount</th>
                <th className="px-5 py-3 font-semibold">ROI</th>
                <th className="px-5 py-3 font-semibold">Expected Return</th>
                <th className="px-5 py-3 font-semibold">Status</th>
                <th className="px-5 py-3 font-semibold">End Date</th>
                <th className="px-5 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {investments.map((inv) => {
                const invUser = users.find((u) => u.user_id === inv.user_id);
                const invPkg = packages.find((p) => p.package_id === inv.package_id);
                return (
                  <tr key={inv.investment_id} className="hover:bg-slate-50">
                    <td className="px-5 py-3 text-slate-400 font-mono text-xs">{inv.investment_id.slice(0, 12)}...</td>
                    <td className="px-5 py-3 text-slate-700 text-sm">{invUser?.email || "Unknown"}</td>
                    <td className="px-5 py-3 text-slate-700 text-sm">{invPkg?.name || "Unknown"}</td>
                    <td className="px-5 py-3 text-slate-700 font-mono text-sm">${inv.amount.toLocaleString()}</td>
                    <td className="px-5 py-3 text-emerald-600 font-mono text-sm">{inv.roi_percentage}%</td>
                    <td className="px-5 py-3 text-amber-600 font-mono text-sm">${inv.expected_return.toLocaleString()}</td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        inv.status === "active" ? "bg-emerald-100 text-emerald-700" :
                        inv.status === "pending_maturity" ? "bg-amber-100 text-amber-700" :
                        inv.status === "completed" ? "bg-blue-100 text-blue-700" :
                        "bg-rose-100 text-rose-700"
                      }`}>{inv.status === "pending_maturity" ? "Pending Maturity" : inv.status}</span>
                    </td>
                    <td className="px-5 py-3 text-slate-500 text-sm">{new Date(inv.end_date).toLocaleDateString()}</td>
                    <td className="px-5 py-3">
                      {inv.status === "pending_maturity" && (
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-emerald-500 hover:text-emerald-600" onClick={() => handleApproveMaturity(inv.investment_id)} data-testid={`approve-maturity-${inv.investment_id}`} title="Approve & credit user">
                            <Check className="w-4 h-4" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-500 hover:text-rose-600" onClick={() => handleRejectMaturity(inv.investment_id)} title="Reject & set back to active">
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};
